import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Description,
    FieldError,
    Fieldset,
    Input,
    Label,
    TextField,
} from '@heroui/react';
import { DatePickerField } from '@/components/datePickerField';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ROW_DRAWER_UNITS,
    ROW_DRAWER_VAT_TYPES,
    type RowDrawerField,
    type RowDrawerForm,
    type RowDrawerNumberField,
} from './rowDrawerForm';
import { RowDrawerPicker } from './RowDrawerPicker';
import { RowDrawerSelect } from './RowDrawerSelect';
import type { RowDrawerCatalogStatus, RowDrawerModel } from './useRowDrawer';

type RowDrawerFieldsProps = {
    model: RowDrawerModel;
    catalogStatus: RowDrawerCatalogStatus;
    onFieldChange: <Field extends RowDrawerField>(
        field: Field,
        value: RowDrawerForm[Field],
    ) => void;
    onAxisChange: (attributeTypeId: string, key: string | null) => void;
};

/** Each field's label. */
export const ROW_DRAWER_FIELD_LABEL_KEY = {
    name: 'catalogImport.products.drawer.fields.name',
    category: 'catalogImport.products.drawer.fields.category',
    brand: 'catalogImport.products.drawer.fields.brand',
    unit: 'catalogImport.products.drawer.fields.unit',
    vatType: 'catalogImport.products.drawer.fields.vatType',
    barcode: 'catalogImport.products.drawer.fields.barcode',
    salePrice: 'catalogImport.products.drawer.fields.salePrice',
    wholesalePrice: 'catalogImport.products.drawer.fields.wholesalePrice',
    minimumQuantity: 'catalogImport.products.drawer.fields.minimumQuantity',
    lotNumber: 'catalogImport.products.drawer.fields.lotNumber',
    quantity: 'catalogImport.products.drawer.fields.quantity',
    cost: 'catalogImport.products.drawer.fields.cost',
    purchaseDate: 'catalogImport.products.drawer.fields.purchaseDate',
    expiryDate: 'catalogImport.products.drawer.fields.expiryDate',
    saleDueDate: 'catalogImport.products.drawer.fields.saleDueDate',
    supplier: 'catalogImport.products.drawer.fields.supplier',
} as const;

/** The products page's unit words. */
export const ROW_DRAWER_UNIT_LABEL_KEY: Record<
    (typeof ROW_DRAWER_UNITS)[number],
    string
> = {
    Piece: 'products.detail.units.piece',
    Kilogram: 'products.detail.units.kilogram',
    Gram: 'products.detail.units.gram',
    Liter: 'products.detail.units.liter',
    Milliliter: 'products.detail.units.milliliter',
    Meter: 'products.detail.units.meter',
    Pack: 'products.detail.units.pack',
};

export const ROW_DRAWER_VAT_LABEL_KEY: Record<
    (typeof ROW_DRAWER_VAT_TYPES)[number],
    string
> = {
    standard: 'catalogImport.products.drawer.vat.standard',
    exempt: 'catalogImport.products.drawer.vat.exempt',
};

/** A field's node name: `salePrice` → `catalog-import-row-drawer-sale-price`. */
const testIdOf = (field: string): string =>
    `catalog-import-row-drawer-${field.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;

/**
 * The drawer's three groups of fields.
 *
 * - **Product** (name, category, brand, unit, VAT): one product per SKU, so
 *   the group says "Applies to all 3 rows of SKU GA55" when the row has
 *   siblings, else "This row only".
 * - **Variant** (barcode, one select per variation axis of the category the
 *   form shows and nothing else, sale and wholesale price, minimum quantity)
 *   and **Batch** (lot number, quantity, cost, the three dates, supplier):
 *   this row only.
 *
 * The pickers wait while the catalog loads. A number field whose text is
 * not a number says so, and Save waits. The grids align their fields to the
 * bottom, so a label that wraps (Georgian's "გაყიდვის ბოლო ვადა" in a third
 * of the drawer) does not push its input out of line with its neighbours'.
 */
export const RowDrawerFields: React.FC<RowDrawerFieldsProps> = ({
    model,
    catalogStatus,
    onFieldChange,
    onAxisChange,
}) => {
    const { t } = useTranslation('pages');
    const { form, errors } = model;
    const pickersWait = catalogStatus !== 'ready';
    const groupSize = model.groupRows.length;

    const textField = (
        field: 'name' | 'barcode' | 'lotNumber',
        placeholder?: string,
    ) => (
        <TextField
            className="flex w-full flex-col gap-1"
            onChange={(value) => onFieldChange(field, value)}
            value={form[field]}
            {...getNodeTestIdProp(testIdOf(field))}
        >
            <Label>{t(ROW_DRAWER_FIELD_LABEL_KEY[field])}</Label>
            <Input placeholder={placeholder} />
        </TextField>
    );

    const numberField = (field: RowDrawerNumberField) => (
        <TextField
            className="flex w-full flex-col gap-1"
            isInvalid={errors[field] === true}
            onChange={(value) => onFieldChange(field, value)}
            value={form[field]}
            {...getNodeTestIdProp(testIdOf(field))}
        >
            <Label>{t(ROW_DRAWER_FIELD_LABEL_KEY[field])}</Label>
            <Input className="tabular-nums" inputMode="decimal" />
            <FieldError>
                {t('catalogImport.products.drawer.errors.number')}
            </FieldError>
        </TextField>
    );

    const dateField = (
        field: 'purchaseDate' | 'expiryDate' | 'saleDueDate',
    ) => (
        <DatePickerField
            id={testIdOf(field)}
            label={t(ROW_DRAWER_FIELD_LABEL_KEY[field])}
            name={field}
            onChange={(value) => onFieldChange(field, value)}
            value={form[field]}
        />
    );

    return (
        <>
            <Fieldset
                className="w-full"
                {...getNodeTestIdProp('catalog-import-row-drawer-product')}
            >
                <Fieldset.Legend>
                    {t('catalogImport.products.drawer.groups.product')}
                </Fieldset.Legend>
                <Description
                    {...getNodeTestIdProp(
                        'catalog-import-row-drawer-product-scope',
                    )}
                >
                    {groupSize > 1
                        ? t('catalogImport.products.drawer.groups.groupScope', {
                              sku: model.sku,
                              rows: countParam(groupSize),
                          })
                        : t('catalogImport.products.drawer.groups.rowScope')}
                </Description>
                <Fieldset.Group className="flex flex-col gap-4">
                    {textField('name')}
                    <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-2">
                        <RowDrawerPicker
                            field="category"
                            isDisabled={pickersWait}
                            label={t(ROW_DRAWER_FIELD_LABEL_KEY.category)}
                            onChange={(key) =>
                                onFieldChange('categoryKey', key)
                            }
                            options={model.categoryOptions}
                            value={form.categoryKey}
                        />
                        <RowDrawerPicker
                            field="brand"
                            isDisabled={pickersWait}
                            label={t(ROW_DRAWER_FIELD_LABEL_KEY.brand)}
                            onChange={(key) => onFieldChange('brandKey', key)}
                            options={model.brandOptions}
                            value={form.brandKey}
                        />
                        <RowDrawerSelect
                            field="unit"
                            label={t(ROW_DRAWER_FIELD_LABEL_KEY.unit)}
                            onChange={(key) => onFieldChange('unit', key)}
                            options={ROW_DRAWER_UNITS.map((unit) => ({
                                id: unit,
                                label: t(ROW_DRAWER_UNIT_LABEL_KEY[unit]),
                            }))}
                            value={form.unit}
                        />
                        <RowDrawerSelect
                            field="vat"
                            label={t(ROW_DRAWER_FIELD_LABEL_KEY.vatType)}
                            onChange={(key) => onFieldChange('vatType', key)}
                            options={ROW_DRAWER_VAT_TYPES.map((vat) => ({
                                id: vat,
                                label: t(ROW_DRAWER_VAT_LABEL_KEY[vat]),
                            }))}
                            value={form.vatType}
                        />
                    </div>
                </Fieldset.Group>
            </Fieldset>

            <Fieldset
                className="w-full"
                {...getNodeTestIdProp('catalog-import-row-drawer-variant')}
            >
                <Fieldset.Legend>
                    {t('catalogImport.products.drawer.groups.variant')}
                </Fieldset.Legend>
                <Description>
                    {t('catalogImport.products.drawer.groups.rowScope')}
                </Description>
                <Fieldset.Group className="flex flex-col gap-4">
                    {textField('barcode')}
                    {model.axes.length === 0 ? null : (
                        <div
                            className="grid grid-cols-2 items-end gap-4"
                            {...getNodeTestIdProp(
                                'catalog-import-row-drawer-axes',
                            )}
                        >
                            {model.axes.map((axis) => (
                                <RowDrawerSelect
                                    field={`axis-${axis.attributeTypeId}`}
                                    key={axis.attributeTypeId}
                                    label={axis.name}
                                    onChange={(key) =>
                                        onAxisChange(axis.attributeTypeId, key)
                                    }
                                    options={axis.options}
                                    value={
                                        form.axisValues[axis.attributeTypeId] ??
                                        null
                                    }
                                />
                            ))}
                        </div>
                    )}
                    <div className="grid grid-cols-2 items-end gap-4 sm:grid-cols-3">
                        {numberField('salePrice')}
                        {numberField('wholesalePrice')}
                        {numberField('minimumQuantity')}
                    </div>
                </Fieldset.Group>
            </Fieldset>

            <Fieldset
                className="w-full"
                {...getNodeTestIdProp('catalog-import-row-drawer-batch')}
            >
                <Fieldset.Legend>
                    {t('catalogImport.products.drawer.groups.batch')}
                </Fieldset.Legend>
                <Description>
                    {t('catalogImport.products.drawer.groups.rowScope')}
                </Description>
                <Fieldset.Group className="flex flex-col gap-4">
                    <div className="grid grid-cols-2 items-end gap-4 sm:grid-cols-3">
                        <div className="col-span-2 sm:col-span-1">
                            {textField(
                                'lotNumber',
                                t(
                                    'catalogImport.products.drawer.fields.lotNumberPlaceholder',
                                ),
                            )}
                        </div>
                        {numberField('quantity')}
                        {numberField('cost')}
                    </div>
                    <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-3">
                        {dateField('purchaseDate')}
                        {dateField('expiryDate')}
                        {dateField('saleDueDate')}
                    </div>
                    <RowDrawerPicker
                        field="supplier"
                        isDisabled={pickersWait}
                        label={t(ROW_DRAWER_FIELD_LABEL_KEY.supplier)}
                        onChange={(key) => onFieldChange('supplierKey', key)}
                        options={model.supplierOptions}
                        value={form.supplierKey}
                    />
                </Fieldset.Group>
            </Fieldset>
        </>
    );
};
